import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Reveal } from "../components/ui/reveal";
import { cn } from "../lib/utils";
import { MediaSlotView } from "../render/context";
import { resolveSlotMedia } from "../render/slot-media";
/**
 * The two-column opening itself, without block chrome — so a component harness
 * renders exactly what a page renders.
 *
 * The lockup and one 4:3 photograph share the content width: 542px of copy, a
 * 90px gutter, then a 500px picture, per the prototype's Patients hero
 * (R3 2711:2613). Below 1161px the two stack, the picture going full width.
 *
 * The type is the plain {@link HeroBlock}'s, deliberately — 96px display at
 * weight 300, a 20px body description, the same narrow-screen steps. This block
 * is the SAME opening with a picture beside it, so its typography is not a
 * second opinion about how a page opens.
 *
 * The title is a real `<h1>`: this is what opens the page, and a page-opening
 * hero whose title is ordinary text leaves the page with nothing to navigate by.
 *
 * Breakpoints are `@min-[901px]` / `@min-[1161px]` because the prototype's
 * breaks are `width <= 900px` and `width <= 1160px` and Tailwind's `@min-[N]`
 * excludes N. Sizing is in `cqw`, not `vw`, so the CMS canvas device frames
 * reflow truthfully; the container they read is the block's own section
 * ({@link HeroMedia2ColumnBlock}), so the two-column layout does not depend on a
 * wrapper the caller may not have.
 */
export function HeroMedia2ColumnView({ data, titleId, delayBase = 0.1, }) {
    const { eyebrow, title, description, media } = data;
    const step = 0.12;
    return (_jsxs("div", { className: cn(
        // The gutters sit INSIDE the 1300px cap, as they do in the prototype:
        // the cap bounds the whole padded container, so at 1440 the row is 992px
        // wide (1300 − 154 × 2), not 1132. Putting the padding on the band
        // outside the cap would widen the copy column by 140px.
        "mx-auto flex w-full max-w-[1300px] flex-col items-start gap-8", "px-[6.667cqw]", "@min-[1161px]:flex-row @min-[1161px]:items-start @min-[1161px]:gap-[90px]", "@min-[1161px]:px-[clamp(24px,10.694cqw,154px)]"), children: [_jsx("div", { className: "w-full @min-[1161px]:w-auto @min-[1161px]:flex-[0_1_542px]", children: _jsxs("div", { className: cn("mx-auto flex w-[clamp(200px,100%,542px)] flex-col items-center gap-4 text-center", "@min-[901px]:mx-0 @min-[901px]:items-start @min-[901px]:text-left"), children: [eyebrow ? (_jsx(Reveal, { delay: delayBase, children: _jsx("p", { "data-hero-media-2-column-eyebrow": true, className: "type-eyebrow-lg m-0 mb-2", children: eyebrow }) })) : null, _jsx(Reveal, { delay: delayBase + step, children: _jsx("h1", { id: titleId, "data-hero-media-2-column-title": true, className: "m-0 font-display text-[clamp(52px,13.5cqw,64px)] font-light leading-[1.04] tracking-[-0.02em] text-brand-blue @min-[901px]:text-[96px]", children: title }) }), description ? (_jsx(Reveal, { delay: delayBase + step * 2, children: _jsx("p", { "data-hero-media-2-column-description": true, className: "m-0 w-full font-sans text-lg font-light leading-[1.55] text-foreground @min-[901px]:text-xl", children: description }) })) : null] }) }), _jsx("div", { "data-hero-media-2-column-media": true, className: "aspect-[4/3] w-full overflow-hidden rounded-[15px] @min-[1161px]:w-[500px] @min-[1161px]:flex-[0_0_500px]", children: _jsx(MediaSlotView, { image: media, ratio: "4 / 3", label: "Hero media", className: "size-full object-cover", emptyRender: null }) })] }));
}
/**
 * The block as a page renders it.
 *
 * Horizontal gutters stay inside the 1300px content cap. BlockWrapper owns
 * the outer vertical spacing.
 */
export function HeroMedia2ColumnBlock({ data, titleId, }) {
    return (_jsx("section", { 
        // Its OWN container, not one inherited from BlockRenderer. Callers reach
        // this block directly — its Storybook story does — with no `@container`
        // above it, and every `@min-[…]` below would silently never match: the
        // desktop layout would simply not exist there. Nesting inside
        // BlockRenderer's wrapper is harmless: both are the block's own width.
        className: "@container w-full bg-transparent", "aria-labelledby": titleId, children: _jsx(HeroMedia2ColumnView, { data: data, titleId: titleId }) }));
}
export function heroMedia2ColumnFromBlock(block, resolve) {
    const scalars = block.scalars ?? {};
    return {
        eyebrow: scalars.eyebrow?.trim() || undefined,
        title: scalars.heading || scalars.title || "",
        description: scalars.subheading || scalars.description || undefined,
        // One declared target, so this resolves to a single framing every screen
        // sees.
        media: resolveSlotMedia(block, "media", resolve),
    };
}
export const heroMedia2ColumnBlockEntry = {
    Component: HeroMedia2ColumnBlock,
    fromBlock: heroMedia2ColumnFromBlock,
};
//# sourceMappingURL=hero-media-2-column.js.map